import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Typography, Table, Button, Input, Select, Switch, Space, Tag, Popconfirm, Tabs, Checkbox, Dropdown,
  Alert, Empty, Tooltip, Radio, Collapse, theme, message,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, ThunderboltOutlined, FilterOutlined, PlayCircleOutlined,
  ArrowUpOutlined, ArrowDownOutlined, CheckCircleFilled, CloseCircleFilled, ExperimentOutlined,
  BranchesOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useBackFallback } from '../components/BackChip';
import api from '../api';
import { formatTS } from '../utils/time';

// Admin > Help Desk Automations: When -> If -> Then rules, run by the server on
// Help Desk events (routes/helpdesk_automations.py). Each rule belongs to a queue
// and runs on that queue's tickets; a QA Admin sees the rules of the queues they
// are a member of, an admin every queue's. The list, and a builder: the
// rule as a flow of cards on the left, the chosen card's settings on the right,
// a Test that shows what the rule would do to a real ticket without changing it,
// and the rule's run log.

const TRIGGER_HELP = {
  ticket_created: 'When a ticket is raised (by a person or by another automation).',
  status_changed: "When a ticket's status changes. Leave From / To empty for any.",
  assigned: "When a ticket's Assigned To changes.",
  comment_added: 'When someone comments on a ticket.',
};
const CONDITION_TYPES = [
  { value: 'request_type', label: 'Request Type is' },
  { value: 'status', label: 'Status is' },
  { value: 'answer', label: 'A form answer' },
  { value: 'created_by_automation', label: 'Made by an automation' },
  { value: 'assigned_by_other', label: 'Assigned by someone else' },
];
const ACTION_TYPES = [
  { value: 'create_ticket', label: 'Create a ticket', help: 'A ticket of another Request Type (in its queue), linked to this one.' },
  { value: 'send_email', label: 'Send an email', help: 'To people on the ticket, its queue, or anyone you name.' },
  { value: 'add_comment', label: 'Add a comment', help: 'A public comment or an internal note on this ticket.' },
  { value: 'set_status', label: 'Set the status', help: "Change this ticket's status." },
  { value: 'assign', label: 'Assign it', help: 'To a member of its queue.' },
  { value: 'add_watchers', label: 'Add watchers', help: 'People who should follow this ticket.' },
];
const ANSWER_OPS = [
  { value: 'equals', label: 'is' }, { value: 'not_equals', label: 'is not' }, { value: 'contains', label: 'contains' },
  { value: 'empty', label: 'is empty' }, { value: 'not_empty', label: 'is not empty' },
];
const PEOPLE = [
  { value: 'impacted', label: 'Impacted Employee' }, { value: 'applicable', label: 'Applicable Employee' },
  { value: 'reporter', label: 'Raised by' }, { value: 'assignee', label: 'Assigned to' },
];
const EMAIL_TO = [
  { value: 'impacted', label: 'Impacted Employee' }, { value: 'reporter', label: 'Raised by' },
  { value: 'assignee', label: 'Assigned to' }, { value: 'watchers', label: 'Watchers' },
  { value: 'queue_members', label: "The ticket's queue members" },
];
const NEW_ACTION = {
  create_ticket: { impacted: 'impacted', link: true, copy_answers: true, notify_impacted: true },
  send_email: { to: ['impacted'], subject: 'Harbor {{ticket.key}}: {{ticket.subject}}', body: '' },
  add_comment: { visibility: 'internal', text: '' },
  set_status: { status: 'in_progress' },
  assign: {},
  add_watchers: { user_ids: [] },
};
const OUTCOME = {
  success: { color: 'green', label: 'Done' }, partial: { color: 'orange', label: 'Partly done' },
  error: { color: 'red', label: 'Failed' }, skipped: { color: 'default', label: 'Skipped' },
};
const EMPTY_RULE = { name: '', description: '', is_active: true, queue_id: null,
                     definition: { trigger: { type: 'ticket_created' }, conditions: [], actions: [] } };

const names = (ids, list, key = 'ID', label = 'Name') =>
  (ids || []).map((id) => list.find((x) => x[key] === id)?.[label] ?? `#${id}`).join(' or ') || '…';

// ---------- one-line summaries, used on the cards and in the list ----------
function triggerText(tr, meta) {
  const st = (v) => names(v, meta.statuses, 'value', 'label');
  if (tr.type === 'status_changed') {
    const from = tr.from?.length ? ` from ${st(tr.from)}` : '';
    const to = tr.to?.length ? ` to ${st(tr.to)}` : '';
    return `Status changed${from}${to}`;
  }
  if (tr.type === 'assigned') return { assigned: 'Assigned to someone', unassigned: 'Unassigned' }[tr.to] || 'Assignee changed';
  if (tr.type === 'comment_added') {
    return { public: 'Public comment added', internal: 'Internal note added', email: 'Email comment sent' }[tr.visibility] || 'Comment added';
  }
  return 'Ticket created';
}
function conditionText(c, meta) {
  if (c.type === 'request_type') return `Request Type is ${names(c.ids, meta.request_types)}`;
  if (c.type === 'status') return `Status is ${names(c.statuses, meta.statuses, 'value', 'label')}`;
  if (c.type === 'answer') {
    const q = meta.questions.find((x) => x.ID === c.question_id);
    const op = ANSWER_OPS.find((o) => o.value === (c.op || 'equals'))?.label;
    return `${q ? q.QuestionText : 'An answer'} ${op}${['empty', 'not_empty'].includes(c.op) ? '' : ` “${c.value || '…'}”`}`;
  }
  if (c.type === 'created_by_automation') return c.value === false ? 'Made by a person' : 'Made by an automation';
  if (c.type === 'assigned_by_other') return 'Assigned by someone else';
  return c.type;
}
function actionText(a, meta) {
  const user = (id) => meta.users.find((u) => u.ID === id)?.DisplayName || '…';
  if (a.type === 'create_ticket') {
    const f = meta.request_types.find((x) => x.ID === a.request_type_id);
    return f ? `Create a ${f.Name} ticket in ${f.QueueName || 'its queue'}` : 'Create a ticket';
  }
  if (a.type === 'send_email') {
    const to = [...(a.to || []).map((v) => EMAIL_TO.find((x) => x.value === v)?.label),
                ...(a.user_ids || []).map(user), ...(a.emails || [])].filter(Boolean);
    return `Email ${to.join(', ') || '…'}`;
  }
  if (a.type === 'add_comment') return a.visibility === 'public' ? 'Add a public comment' : 'Add an internal note';
  if (a.type === 'set_status') return `Set the status to ${meta.statuses.find((s) => s.value === a.status)?.label || '…'}`;
  if (a.type === 'assign') return a.user_id ? `Assign to ${user(a.user_id)}` : 'Unassign';
  if (a.type === 'add_watchers') return `Add watchers: ${(a.user_ids || []).map(user).join(', ') || '…'}`;
  return a.type;
}

// ---------- a text box with an Insert variable menu ----------
function VarInput({ value, onChange, variables, area, rows = 4, placeholder }) {
  const ref = useRef(null);
  const insert = (name) => {
    const el = ref.current?.resizableTextArea?.textArea || ref.current?.input;
    const v = value || '';
    const at = el && typeof el.selectionStart === 'number' ? el.selectionStart : v.length;
    const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : at;
    const token = `{{${name}}}`;
    onChange(v.slice(0, at) + token + v.slice(end));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange?.(at + token.length, at + token.length); });
  };
  const menu = {
    items: variables.map((g) => ({
      key: g.group, type: 'group', label: g.group,
      children: g.items.map((v) => ({ key: v.name, label: <span><code>{`{{${v.name}}}`}</code> <span style={{ opacity: 0.65 }}>{v.label}</span></span> })),
    })),
    onClick: ({ key }) => insert(key),
    style: { maxHeight: 360, overflowY: 'auto' },
  };
  const btn = <Dropdown menu={menu} trigger={['click']}><Button size="small" icon={<BranchesOutlined />}>Insert variable</Button></Dropdown>;
  return (
    <div>
      {area
        ? <Input.TextArea ref={ref} rows={rows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
        : <Input ref={ref} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />}
      <div style={{ marginTop: 4 }}>{btn}</div>
    </div>
  );
}

function Field({ label, help, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {children}
      {help && <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>{help}</div>}
    </div>
  );
}

// ---------- the settings of the selected card ----------
function TriggerEditor({ tr, set, meta }) {
  return (
    <>
      <Field label="When" help={TRIGGER_HELP[tr.type]}>
        <Select style={{ width: '100%' }} value={tr.type} options={meta.triggers}
                onChange={(type) => set({ type })} />
      </Field>
      {tr.type === 'status_changed' && (
        <>
          <Field label="From (optional)">
            <Select mode="multiple" allowClear style={{ width: '100%' }} value={tr.from || []} options={meta.statuses}
                    placeholder="Any status" onChange={(from) => set({ ...tr, from })} />
          </Field>
          <Field label="To (optional)">
            <Select mode="multiple" allowClear style={{ width: '100%' }} value={tr.to || []} options={meta.statuses}
                    placeholder="Any status" onChange={(to) => set({ ...tr, to })} />
          </Field>
        </>
      )}
      {tr.type === 'assigned' && (
        <Field label="Only when">
          <Radio.Group value={tr.to || 'any'} onChange={(e) => set({ ...tr, to: e.target.value })}
                       options={[{ value: 'any', label: 'Any change' }, { value: 'assigned', label: 'Assigned to someone' },
                                 { value: 'unassigned', label: 'Unassigned' }]} />
        </Field>
      )}
      {tr.type === 'comment_added' && (
        <Field label="Kind of comment">
          <Radio.Group value={tr.visibility || 'any'} onChange={(e) => set({ ...tr, visibility: e.target.value })}
                       options={[{ value: 'any', label: 'Any' }, { value: 'public', label: 'Public' },
                                 { value: 'internal', label: 'Internal note' }, { value: 'email', label: 'Email' }]} />
        </Field>
      )}
    </>
  );
}

function ConditionEditor({ c, set, meta, forms }) {
  const q = meta.questions.find((x) => x.ID === c.question_id);
  return (
    <>
      <Field label="If">
        <Select style={{ width: '100%' }} value={c.type} options={CONDITION_TYPES} onChange={(type) => set({ type })} />
      </Field>
      {c.type === 'request_type' && (
        <Field label="Request Type" help="Matches any of those picked.">
          <Select mode="multiple" style={{ width: '100%' }} value={c.ids || []} optionFilterProp="label"
                  options={forms.map((f) => ({ value: f.ID, label: f.Name }))}
                  onChange={(ids) => set({ ...c, ids })} />
        </Field>
      )}
      {c.type === 'status' && (
        <Field label="Status" help="The ticket's status when the trigger happens.">
          <Select mode="multiple" style={{ width: '100%' }} value={c.statuses || []} options={meta.statuses}
                  onChange={(statuses) => set({ ...c, statuses })} />
        </Field>
      )}
      {c.type === 'answer' && (
        <>
          <Field label="Question">
            <Select showSearch style={{ width: '100%' }} value={c.question_id} optionFilterProp="label"
                    options={forms.filter((f) => f.Questions.length).map((f) => ({
                      label: f.Name, options: f.Questions.map((x) => ({ value: x.ID, label: x.QuestionText })),
                    }))}
                    onChange={(question_id) => set({ ...c, question_id })} />
          </Field>
          <Field label="Answer">
            <Space.Compact style={{ width: '100%' }}>
              <Select style={{ width: 140 }} value={c.op || 'equals'} options={ANSWER_OPS} onChange={(op) => set({ ...c, op })} />
              {!['empty', 'not_empty'].includes(c.op) && (q?.Options?.length
                ? <Select style={{ flex: 1 }} value={c.value} options={q.Options.map((o) => ({ value: o, label: o }))}
                          onChange={(value) => set({ ...c, value })} />
                : <Input style={{ flex: 1 }} value={c.value} onChange={(e) => set({ ...c, value: e.target.value })} />)}
            </Space.Compact>
          </Field>
        </>
      )}
      {c.type === 'created_by_automation' && (
        <Field label="The ticket was made by" help="Useful to keep an automation from acting on tickets other automations made.">
          <Radio.Group value={c.value !== false} onChange={(e) => set({ ...c, value: e.target.value })}
                       options={[{ value: true, label: 'An automation' }, { value: false, label: 'A person' }]} />
        </Field>
      )}
      {c.type === 'assigned_by_other' && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Skips a tech assigning the ticket to themselves. Use with “Assignee changed” to tell someone a ticket was handed to them.
        </Typography.Text>
      )}
    </>
  );
}

function ActionEditor({ a, set, meta, variables }) {
  const target = meta.request_types.find((f) => f.ID === a.request_type_id);
  const users = meta.users.map((u) => ({ value: u.ID, label: u.DisplayName }));
  return (
    <>
      <Field label="Then" help={ACTION_TYPES.find((x) => x.value === a.type)?.help}>
        <Select style={{ width: '100%' }} value={a.type} options={ACTION_TYPES}
                onChange={(type) => set({ type, ...NEW_ACTION[type] })} />
      </Field>

      {a.type === 'create_ticket' && (
        <>
          <Field label="Request Type of the new ticket" help={target ? `It goes to ${target.QueueName || 'its queue'}.` : null}>
            <Select showSearch style={{ width: '100%' }} value={a.request_type_id} optionFilterProp="label"
                    options={meta.request_types.map((f) => ({ value: f.ID, label: `${f.Name}${f.QueueName ? ` (${f.QueueName})` : ''}` }))}
                    onChange={(request_type_id) => set({ ...a, request_type_id })} />
          </Field>
          <Field label="Impacted Employee of the new ticket">
            <Select style={{ width: '100%' }} value={a.impacted || 'impacted'}
                    options={[...PEOPLE.map((p) => ({ ...p, label: `This ticket's ${p.label}` })), { value: 'user', label: 'A particular person' }]}
                    onChange={(impacted) => set({ ...a, impacted })} />
            {a.impacted === 'user' && (
              <Select showSearch style={{ width: '100%', marginTop: 6 }} value={a.impacted_user_id} options={users}
                      optionFilterProp="label" onChange={(impacted_user_id) => set({ ...a, impacted_user_id })} />
            )}
          </Field>
          {!!target?.UsesApplicable && (
            <Field label="Applicable Employee of the new ticket" help={`${target.Name} is about someone (an Applicable Employee).`}>
              <Select style={{ width: '100%' }} value={a.applicable}
                      options={[...PEOPLE.map((p) => ({ ...p, label: `This ticket's ${p.label}` })), { value: 'user', label: 'A particular person' }]}
                      onChange={(applicable) => set({ ...a, applicable })} />
              {a.applicable === 'user' && (
                <Select showSearch style={{ width: '100%', marginTop: 6 }} value={a.applicable_user_id} options={users}
                        optionFilterProp="label" onChange={(applicable_user_id) => set({ ...a, applicable_user_id })} />
              )}
            </Field>
          )}
          <Field label="Subject" help="Left empty: “<Request Type> for <employee>”.">
            <VarInput value={a.subject} onChange={(subject) => set({ ...a, subject })} variables={variables}
                      placeholder="Equipment for {{impacted.name}}" />
          </Field>
          <Field label="Description">
            <VarInput area value={a.description} onChange={(description) => set({ ...a, description })} variables={variables} />
          </Field>
          <Field label="Add Standard Equipment to its Shipments" help="Every asset type marked Standard Equipment, Qty 1.">
            <Select allowClear style={{ width: '100%' }} value={a.standard_equipment} placeholder="No"
                    options={meta.directions.map((d) => ({ value: d, label: d }))}
                    onChange={(standard_equipment) => set({ ...a, standard_equipment })} />
          </Field>
          <Field label="Assign it to (optional)">
            <Select allowClear showSearch style={{ width: '100%' }} value={a.assign_to_user_id} optionFilterProp="label"
                    placeholder="Leave unassigned"
                    options={meta.queue_members.filter((m) => !target || m.QueueID === target.QueueID)
                      .map((m) => ({ value: m.ID, label: m.DisplayName }))}
                    onChange={(assign_to_user_id) => set({ ...a, assign_to_user_id })} />
          </Field>
          <Space direction="vertical">
            <Checkbox checked={a.link !== false} onChange={(e) => set({ ...a, link: e.target.checked })}>
              Link it to this ticket (a follow-up)
            </Checkbox>
            <Checkbox checked={a.copy_answers !== false} onChange={(e) => set({ ...a, copy_answers: e.target.checked })}>
              Copy answers to questions worded the same
            </Checkbox>
            <Checkbox checked={a.notify_impacted !== false} onChange={(e) => set({ ...a, notify_impacted: e.target.checked })}>
              Notify the new ticket's Impacted Employee
            </Checkbox>
          </Space>
        </>
      )}

      {a.type === 'send_email' && (
        <>
          <Field label="To" help="People on the ticket follow its rules: a Restricted queue's ticket only reaches those who may see it, and never the Applicable Employee.">
            <Select mode="multiple" style={{ width: '100%' }} value={a.to || []} options={EMAIL_TO}
                    placeholder="People on the ticket" onChange={(to) => set({ ...a, to })} />
            <Select mode="multiple" showSearch style={{ width: '100%', marginTop: 6 }} value={a.user_ids || []}
                    optionFilterProp="label" options={users} placeholder="Particular people"
                    onChange={(user_ids) => set({ ...a, user_ids })} />
            <Select mode="tags" style={{ width: '100%', marginTop: 6 }} value={a.emails || []} tokenSeparators={[',', ' ', ';']}
                    placeholder="Other addresses (a group mailbox)" onChange={(emails) => set({ ...a, emails })} />
          </Field>
          <Field label="Subject">
            <VarInput value={a.subject} onChange={(subject) => set({ ...a, subject })} variables={variables} />
          </Field>
          <Field label="Message">
            <VarInput area rows={6} value={a.body} onChange={(body) => set({ ...a, body })} variables={variables} />
          </Field>
        </>
      )}

      {a.type === 'add_comment' && (
        <>
          <Field label="Kind">
            <Radio.Group value={a.visibility || 'internal'} onChange={(e) => set({ ...a, visibility: e.target.value })}
                         options={[{ value: 'internal', label: 'Internal note' }, { value: 'public', label: 'Public comment' }]} />
          </Field>
          <Field label="Text" help="After a Create a ticket action, {{created.key}} is the new ticket's key.">
            <VarInput area value={a.text} onChange={(text) => set({ ...a, text })} variables={variables} />
          </Field>
        </>
      )}

      {a.type === 'set_status' && (
        <Field label="Status">
          <Select style={{ width: '100%' }} value={a.status} options={meta.statuses} onChange={(status) => set({ ...a, status })} />
        </Field>
      )}

      {a.type === 'assign' && (
        <Field label="Assign to" help="Must be an active member of the ticket's queue when it runs. Empty unassigns.">
          <Select allowClear showSearch style={{ width: '100%' }} value={a.user_id} optionFilterProp="label"
                  placeholder="Unassign" options={users} onChange={(user_id) => set({ ...a, user_id })} />
        </Field>
      )}

      {a.type === 'add_watchers' && (
        <Field label="Watchers" help="Never the Applicable Employee.">
          <Select mode="multiple" showSearch style={{ width: '100%' }} value={a.user_ids || []} optionFilterProp="label"
                  options={users} onChange={(user_ids) => set({ ...a, user_ids })} />
        </Field>
      )}
    </>
  );
}

// ---------- the flow of cards ----------
function FlowCard({ kind, icon, label, text, selected, onClick, onRemove, onUp, onDown, n, token }) {
  const color = { when: token.colorWarning, if: token.colorInfo, then: token.colorSuccess }[kind];
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
        background: token.colorBgContainer, border: `1px solid ${selected ? color : token.colorBorderSecondary}`,
        boxShadow: selected ? `0 0 0 2px ${color}33` : 'none', borderLeft: `4px solid ${color}`,
      }}
    >
      <span style={{ color, fontSize: 18 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color, textTransform: 'uppercase' }}>
          {label}{n ? ` ${n}` : ''}
        </div>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</div>
      </div>
      <span onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 2 }}>
        {onUp && <Button size="small" type="text" icon={<ArrowUpOutlined />} onClick={onUp} aria-label="Move up" />}
        {onDown && <Button size="small" type="text" icon={<ArrowDownOutlined />} onClick={onDown} aria-label="Move down" />}
        {onRemove && <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={onRemove} aria-label="Remove" />}
      </span>
    </div>
  );
}

const Connector = ({ token }) => <div style={{ width: 2, height: 16, background: token.colorBorder, margin: '0 auto' }} />;

// ---------- Test ----------
function TestPanel({ rule, meta, saved }) {
  const [ticket, setTicket] = useState('');
  const [ctx, setCtx] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const tr = rule.definition.trigger;
  const run = async (apply) => {
    setBusy(true);
    try {
      const r = await api.post('/api/helpdesk/automations/test', {
        id: rule.id, name: rule.name, queue_id: rule.queue_id, definition: rule.definition, ticket, apply, ...ctx,
      });
      setResult({ ...r.data, applied: apply });
      if (apply) message.success(r.data.matched ? 'Ran on the ticket.' : 'It did not match, so nothing ran.');
    } catch (err) {
      message.error(err.response?.data?.error || 'Test failed.');
    } finally {
      setBusy(false);
    }
  };
  const Mark = ({ ok }) => (ok ? <CheckCircleFilled style={{ color: '#3fb950' }} /> : <CloseCircleFilled style={{ color: '#f85149' }} />);
  return (
    <div>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Try the rule as it is in the builder (saved or not) on a real ticket in its queue. Test shows what would
        happen and changes nothing.
      </Typography.Paragraph>
      <Space wrap align="end">
        <Field label="Ticket">
          <Input style={{ width: 160 }} placeholder={`${meta.queues.find((q) => q.ID === rule.queue_id)?.TicketPrefix || 'HDHR'}-12`} value={ticket} onChange={(e) => setTicket(e.target.value)}
                 onPressEnter={() => ticket && run(false)} />
        </Field>
        {tr.type === 'status_changed' && (
          <>
            <Field label="Pretend it went from">
              <Select style={{ width: 170 }} allowClear options={meta.statuses} value={ctx.from} onChange={(from) => setCtx({ ...ctx, from })} />
            </Field>
            <Field label="to">
              <Select style={{ width: 170 }} allowClear options={meta.statuses} value={ctx.to} onChange={(to) => setCtx({ ...ctx, to })} />
            </Field>
          </>
        )}
        {tr.type === 'comment_added' && (
          <>
            <Field label="Pretend comment">
              <Input style={{ width: 220 }} value={ctx.comment} onChange={(e) => setCtx({ ...ctx, comment: e.target.value })} />
            </Field>
            <Field label="Kind">
              <Select style={{ width: 130 }} value={ctx.mode || 'public'} onChange={(mode) => setCtx({ ...ctx, mode })}
                      options={[{ value: 'public', label: 'Public' }, { value: 'internal', label: 'Internal' }, { value: 'email', label: 'Email' }]} />
            </Field>
          </>
        )}
        <Field label=" ">
          <Space>
            <Button type="primary" icon={<ExperimentOutlined />} loading={busy} disabled={!ticket || !rule.queue_id} onClick={() => run(false)}>Test</Button>
            <Popconfirm title="Run it for real?" description="It really does what it says on this ticket (and is logged)."
                        okText="Run it" disabled={!saved || !ticket} onConfirm={() => run(true)}>
              <Tooltip title={saved ? null : 'Save the automation first'}>
                <Button icon={<PlayCircleOutlined />} disabled={!saved || !ticket} loading={busy}>Run on this ticket</Button>
              </Tooltip>
            </Popconfirm>
          </Space>
        </Field>
      </Space>

      {result && (
        <div style={{ marginTop: 8 }}>
          <Alert type={result.matched ? 'success' : 'warning'} showIcon style={{ marginBottom: 12 }}
                 message={result.matched
                   ? `${result.ticket.key} matches: ${result.applied ? 'it ran' : 'this is what it would do'}.`
                   : `${result.ticket?.key || 'The ticket'} does not match, so nothing would happen.`} />
          <div style={{ display: 'grid', gap: 6 }}>
            <div><Mark ok={result.trigger_ok} /> <b>When</b> {triggerText(rule.definition.trigger, meta)}
              {!result.trigger_ok && <span style={{ opacity: 0.7 }}> (the pretend change above does not fit it)</span>}</div>
            {result.conditions.map((c, i) => (
              <div key={i}><Mark ok={c.ok} /> <b>If</b> {conditionText(rule.definition.conditions[i], meta)}
                <span style={{ opacity: 0.7 }}> — {c.why}</span></div>
            ))}
            {result.actions.map((a, i) => (
              <div key={i}>
                <Mark ok={a.ok} /> <b>Then</b> {a.summary}
                {a.type === 'send_email' && a.detail?.body != null && (
                  <pre style={{ whiteSpace: 'pre-wrap', margin: '6px 0 0 22px', padding: 8, borderRadius: 6,
                                background: 'rgba(127,127,127,.12)', fontFamily: 'inherit' }}>
                    {`To: ${(a.detail.to || []).join(', ')}\nSubject: ${a.detail.subject}\n\n${a.detail.body}`}
                  </pre>
                )}
                {a.type === 'create_ticket' && a.detail?.subject && (
                  <div style={{ margin: '4px 0 0 22px', fontSize: 13, opacity: 0.85 }}>
                    {a.detail.description && <div>Description: {a.detail.description}</div>}
                    {!!a.detail.answers_copied?.length && <div>Answers copied: {a.detail.answers_copied.join(', ')}</div>}
                    {!!a.detail.standard_equipment?.length && <div>Shipments: {a.detail.standard_equipment.join(', ')} ({a.detail.direction})</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
          {!!result.unknown_variables?.length && (
            <Alert type="warning" showIcon style={{ marginTop: 12 }}
                   message={`Not known, so left blank: ${result.unknown_variables.map((v) => `{{${v}}}`).join(', ')}`} />
          )}
        </div>
      )}
    </div>
  );
}

// ---------- run log ----------
function RunLog({ automationId, showRule }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/helpdesk/automations/runs', { params: automationId ? { automation_id: automationId } : {} })
      .then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [automationId]);
  useEffect(() => { load(); }, [load]);
  const columns = [
    { title: 'When', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 160, render: (v) => formatTS(v) },
    ...(showRule ? [{ title: 'Automation', dataIndex: 'AutomationName', key: 'AutomationName', width: 220 }] : []),
    { title: 'Ticket', dataIndex: 'TicketID', key: 'TicketID', width: 110,
      render: (v, r) => (v ? <Link to={`/helpdesk/${v}`}>{r.TicketKey || `HD-${v}`}</Link> : '—') },
    { title: 'Result', dataIndex: 'Outcome', key: 'Outcome', width: 120,
      render: (v, r) => <Space size={4}><Tag color={OUTCOME[v]?.color}>{OUTCOME[v]?.label || v}</Tag>{!!r.IsManual && <Tag>By hand</Tag>}</Space> },
    { title: 'What it did', key: 'did',
      render: (_, r) => (r.Detail?.actions?.length
        ? r.Detail.actions.map((a, i) => (
          <div key={i} style={{ color: a.ok ? undefined : '#f85149' }}>{a.summary}</div>))
        : <span style={{ opacity: 0.7 }}>{r.Detail?.note || r.Detail?.error || '—'}</span>) },
  ];
  return (
    <>
      <div style={{ textAlign: 'right', marginBottom: 8 }}><Button size="small" onClick={load}>Refresh</Button></div>
      <Table rowKey="ID" size="small" dataSource={rows} columns={columns} loading={loading}
             pagination={rows.length > 20 ? { pageSize: 20 } : false}
             locale={{ emptyText: 'Nothing has run yet. A run is kept whenever the trigger happens and the conditions match.' }} />
    </>
  );
}

// ---------- the builder ----------
function Builder({ meta, id }) {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [rule, setRule] = useState(null);
  const [sel, setSel] = useState({ kind: 'when' });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const isNew = id === 'new';

  useEffect(() => {
    if (isNew) {
      // A queue picked on the list (?queue=), or the only queue this user has
      const want = Number(new URLSearchParams(window.location.search).get('queue'));
      const queue_id = meta.myQueues.some((q) => q.ID === want) ? want : (meta.myQueues.length === 1 ? meta.myQueues[0].ID : null);
      setRule({ ...EMPTY_RULE, queue_id, definition: { ...EMPTY_RULE.definition } });
      return;
    }
    api.get(`/api/helpdesk/automations/${id}`).then((r) => setRule({
      id: r.data.ID, name: r.data.Name, description: r.data.Description || '', is_active: !!r.data.IsActive,
      queue_id: r.data.QueueID, definition: r.data.Definition,
    })).catch((err) => message.error(err.response?.data?.error || 'Could not load the automation.'));
  }, [id, isNew, meta.myQueues]);

  const forms = useMemo(() => meta.request_types.filter((f) => rule && f.QueueID === rule.queue_id), [meta, rule]);

  // Variables offered: the standard ones, then the answers of the Request Types
  // this rule is about (all Request Types when it names none).
  const variables = useMemo(() => {
    if (!rule) return [];
    const picked = rule.definition.conditions.filter((c) => c.type === 'request_type').flatMap((c) => c.ids || []);
    const about = forms.filter((f) => !picked.length || picked.includes(f.ID));
    const answers = [...new Set(about.flatMap((f) => f.Questions.map((q) => q.QuestionText)))];
    const by = (prefix) => meta.variables.filter((v) => v.name.startsWith(prefix));
    return [
      { group: 'Ticket', items: by('ticket.') },
      { group: 'People', items: [...by('impacted.'), ...by('applicable.'), ...by('reporter.'), ...by('assignee.')] },
      { group: 'What happened', items: [...by('trigger.'), ...by('created.'), ...by('today')] },
      ...(answers.length ? [{ group: 'Answers', items: answers.map((a) => ({ name: `answer.${a}`, label: '' })) }] : []),
    ];
  }, [rule, meta, forms]);

  if (!rule) return null;
  const def = rule.definition;
  const update = (patch) => { setRule((r) => ({ ...r, ...patch })); setDirty(true); };
  const setDef = (d) => update({ definition: { ...def, ...d } });
  const setList = (key, i, v) => setDef({ [key]: def[key].map((x, j) => (j === i ? v : x)) });
  const setQueue = (queue_id) => {
    const ids = meta.request_types.filter((f) => f.QueueID === queue_id).map((f) => f.ID);
    const qs = meta.questions.filter((q) => ids.includes(q.FormID)).map((q) => q.ID);
    const conditions = def.conditions
      .map((c) => (c.type === 'request_type' ? { ...c, ids: (c.ids || []).filter((x) => ids.includes(x)) } : c))
      .filter((c) => !(c.type === 'request_type' && !c.ids.length) && !(c.type === 'answer' && c.question_id && !qs.includes(c.question_id)));
    update({ queue_id, definition: { ...def, conditions } });
    setSel({ kind: 'when' });
  };
  const queueName = meta.queues.find((q) => q.ID === rule.queue_id)?.Name;
  const remove = (key, i) => { setDef({ [key]: def[key].filter((_, j) => j !== i) }); setSel({ kind: 'when' }); };
  const move = (key, i, d) => {
    const list = [...def[key]];
    [list[i], list[i + d]] = [list[i + d], list[i]];
    setDef({ [key]: list });
    setSel({ kind: 'then', i: i + d });
  };
  const addCondition = () => { setDef({ conditions: [...def.conditions, { type: 'request_type', ids: [] }] }); setSel({ kind: 'if', i: def.conditions.length }); };
  const addAction = (type) => { setDef({ actions: [...def.actions, { type, ...NEW_ACTION[type] }] }); setSel({ kind: 'then', i: def.actions.length }); };

  const save = async () => {
    setSaving(true);
    try {
      const body = { name: rule.name, description: rule.description, is_active: rule.is_active, queue_id: rule.queue_id, definition: def };
      if (rule.id) await api.put(`/api/helpdesk/automations/${rule.id}`, body);
      else {
        const r = await api.post('/api/helpdesk/automations', body);
        setRule((x) => ({ ...x, id: r.data.id }));
        navigate(`/admin/helpdesk-automations/${r.data.id}`, { replace: true });
      }
      setDirty(false);
      message.success('Saved.');
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  let panel = null;
  if (sel.kind === 'when') panel = <TriggerEditor tr={def.trigger} meta={meta} set={(trigger) => setDef({ trigger })} />;
  if (sel.kind === 'if' && def.conditions[sel.i]) {
    panel = <ConditionEditor c={def.conditions[sel.i]} meta={meta} forms={forms} set={(v) => setList('conditions', sel.i, v)} />;
  }
  if (sel.kind === 'then' && def.actions[sel.i]) {
    panel = <ActionEditor a={def.actions[sel.i]} meta={meta} variables={variables} set={(v) => setList('actions', sel.i, v)} />;
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <Select style={{ width: 200 }} placeholder="Queue" value={rule.queue_id} onChange={setQueue}
                status={rule.queue_id ? undefined : 'warning'}
                options={meta.myQueues.map((q) => ({ value: q.ID, label: q.Name }))} />
        <Input style={{ flex: '1 1 280px', fontSize: 18, fontWeight: 600 }} placeholder="Name this automation"
               value={rule.name} onChange={(e) => update({ name: e.target.value })} />
        <Space>
          <span>On</span>
          <Switch checked={rule.is_active} onChange={(is_active) => update({ is_active })} />
          <Button type="primary" loading={saving} onClick={save} disabled={!rule.name.trim() || !rule.queue_id}>
            {dirty || !rule.id ? 'Save' : 'Saved'}
          </Button>
        </Space>
      </div>
      <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} style={{ marginBottom: 16 }} placeholder="What it is for (optional)"
                      value={rule.description} onChange={(e) => update({ description: e.target.value })} />

      <Tabs items={[
        {
          key: 'build', label: 'Build',
          children: (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 300px', maxWidth: 460 }}>
                <FlowCard kind="when" icon={<ThunderboltOutlined />} label="When" token={token}
                          text={queueName ? `${triggerText(def.trigger, meta)} in ${queueName}` : 'Pick the queue first (top left)'}
                          selected={sel.kind === 'when'} onClick={() => setSel({ kind: 'when' })} />
                {def.conditions.map((c, i) => (
                  <React.Fragment key={`c${i}`}>
                    <Connector token={token} />
                    <FlowCard kind="if" icon={<FilterOutlined />} label={i ? 'And if' : 'If'} token={token}
                              text={conditionText(c, meta)} selected={sel.kind === 'if' && sel.i === i}
                              onClick={() => setSel({ kind: 'if', i })} onRemove={() => remove('conditions', i)} />
                  </React.Fragment>
                ))}
                <Connector token={token} />
                <div style={{ textAlign: 'center' }}>
                  <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addCondition}>Condition</Button>
                </div>
                {def.actions.map((a, i) => (
                  <React.Fragment key={`a${i}`}>
                    <Connector token={token} />
                    <FlowCard kind="then" icon={<PlayCircleOutlined />} label="Then" n={def.actions.length > 1 ? i + 1 : null}
                              token={token} text={actionText(a, meta)} selected={sel.kind === 'then' && sel.i === i}
                              onClick={() => setSel({ kind: 'then', i })} onRemove={() => remove('actions', i)}
                              onUp={i > 0 ? () => move('actions', i, -1) : null}
                              onDown={i < def.actions.length - 1 ? () => move('actions', i, 1) : null} />
                  </React.Fragment>
                ))}
                <Connector token={token} />
                <div style={{ textAlign: 'center' }}>
                  <Dropdown trigger={['click']} menu={{
                    items: ACTION_TYPES.map((x) => ({ key: x.value, label: <div><div>{x.label}</div><div style={{ fontSize: 12, opacity: 0.65 }}>{x.help}</div></div> })),
                    onClick: ({ key }) => addAction(key),
                  }}>
                    <Button size="small" type="dashed" icon={<PlusOutlined />}>Action</Button>
                  </Dropdown>
                </div>
              </div>
              <div style={{ flex: '1 1 380px', minWidth: 0, padding: 16, borderRadius: 10,
                            border: `1px solid ${token.colorBorderSecondary}`, background: token.colorBgContainer }}>
                {panel || <Empty description="Pick a card on the left" />}
              </div>
            </div>
          ),
        },
        { key: 'test', label: 'Test', children: <TestPanel rule={rule} meta={meta} saved={!!rule.id && !dirty} /> },
        ...(rule.id ? [{ key: 'log', label: 'Run log', children: <RunLog automationId={rule.id} /> }] : []),
      ]} />
    </>
  );
}

// ---------- the list ----------
function RuleList({ meta, queue, setQueue }) {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/helpdesk/automations').then((r) => setRules(r.data || []))
      .catch(() => message.error('Could not load automations.')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (r, on) => {
    await api.put(`/api/helpdesk/automations/${r.ID}/active`, { is_active: on }).catch(() => message.error('Failed.'));
    load();
  };
  const del = async (r) => {
    await api.delete(`/api/helpdesk/automations/${r.ID}`).catch(() => message.error('Failed.'));
    load();
  };
  const shown = queue ? rules.filter((r) => r.QueueID === queue) : rules;
  const columns = [
    { title: 'Queue', dataIndex: 'QueueName', key: 'QueueName', width: 150 },
    { title: 'Automation', dataIndex: 'Name', key: 'Name', width: 260,
      render: (v, r) => (
        <div>
          <Typography.Link strong onClick={() => navigate(`/admin/helpdesk-automations/${r.ID}`)}>{v}</Typography.Link>
          {r.Description && <div style={{ fontSize: 12, opacity: 0.7 }}>{r.Description}</div>}
        </div>) },
    { title: 'What it does', key: 'flow',
      render: (_, r) => {
        const d = r.Definition || {};
        return (
          <div style={{ fontSize: 13 }}>
            <div><b>When</b> {triggerText(d.trigger || {}, meta)}</div>
            {(d.conditions || []).map((c, i) => <div key={i}><b>{i ? 'And if' : 'If'}</b> {conditionText(c, meta)}</div>)}
            {(d.actions || []).map((a, i) => <div key={i}><b>Then</b> {actionText(a, meta)}</div>)}
          </div>
        );
      } },
    { title: 'Runs', key: 'runs', width: 170,
      render: (_, r) => (r.Runs
        ? <div style={{ fontSize: 12 }}>{r.Runs} run{r.Runs === 1 ? '' : 's'}{r.Errors ? <Tag color="red" style={{ marginLeft: 6 }}>{r.Errors} failed</Tag> : null}
          <div style={{ opacity: 0.7 }}>last {formatTS(r.LastRunAt)}</div></div>
        : <span style={{ fontSize: 12, opacity: 0.6 }}>Not run yet</span>) },
    { title: 'On', dataIndex: 'IsActive', key: 'IsActive', width: 70,
      render: (v, r) => <Switch size="small" checked={!!v} onChange={(on) => toggle(r, on)} /> },
    { title: '', key: 'x', width: 120, align: 'right',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/admin/helpdesk-automations/${r.ID}`)} />
          <Popconfirm title="Delete this automation?" description="Its run log goes too. Tickets it made stay." onConfirm={() => del(r)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>) },
  ];
  return (
    <Tabs items={[
      { key: 'rules', label: 'Automations',
        children: (
          <>
            {meta.myQueues.length > 1 && (
              <Select allowClear placeholder="All my queues" style={{ width: 220, marginBottom: 12 }} value={queue}
                      onChange={(v) => setQueue(v ?? null)} options={meta.myQueues.map((q) => ({ value: q.ID, label: q.Name }))} />
            )}
            <Table rowKey="ID" size="small" dataSource={shown} columns={columns} loading={loading} pagination={false}
                   locale={{ emptyText: meta.myQueues.length
                     ? 'No automations yet. Create one to have the Help Desk act on its own.'
                     : 'You manage the automations of the Help Desk queues you are a member of, and you are not in any.' }} />
          </>
        ) },
      { key: 'log', label: 'Run log', children: <RunLog showRule /> },
    ]} />
  );
}

export default function HelpDeskAutomationsPage() {
  const { id } = useParams();
  // A rule's page: the Back chip (Layout) goes where you came from, else the list.
  useBackFallback(id ? '/admin/helpdesk-automations' : null, 'Help Desk Automations');
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [queue, setQueue] = useState(null);
  useEffect(() => {
    api.get('/api/helpdesk/automations/meta').then((r) => setMeta({
      ...r.data, questions: r.data.request_types.flatMap((f) => f.Questions),
      myQueues: r.data.my_queue_ids ? r.data.queues.filter((q) => r.data.my_queue_ids.includes(q.ID)) : r.data.queues,
    })).catch(() => message.error('Could not load the Help Desk set-up.'));
  }, []);
  if (!meta) return null;
  return (
    <>
      {!id && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>Help Desk Automations</Typography.Title>
            <Typography.Text type="secondary">When something happens to a ticket in a queue, and it fits, do something.</Typography.Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} disabled={!meta.myQueues.length}
                  onClick={() => navigate(`/admin/helpdesk-automations/new${queue ? `?queue=${queue}` : ''}`)}>
            Create automation
          </Button>
        </div>
      )}
      {id ? <Builder meta={meta} id={id} key={id === 'new' ? 'new' : 'edit'} /> : <RuleList meta={meta} queue={queue} setQueue={setQueue} />}
      {!id && (
        <Collapse ghost style={{ marginTop: 16 }} items={[{
          key: 'vars', label: 'Variables you can use in text',
          children: (
            <div style={{ columns: '2 280px', fontSize: 13 }}>
              {meta.variables.map((v) => <div key={v.name}><code>{`{{${v.name}}}`}</code> {v.label}</div>)}
              <div><code>{'{{answer.<question>}}'}</code> the answer to a question, by its wording (any letter case)</div>
            </div>
          ),
        }]} />
      )}
    </>
  );
}
